import { useState, useMemo } from 'react';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import useGetDeviceVideoSettings from '../../../API/hooks/videos/useGetDeviceVideoSettings';
import { LiveStreamProtocol } from '../../../API/types/Videos';
import useLocalStorage from '../../../API/hooks/useLocalStorage';
import { getLocalStorageKey } from '../../../utils/storageUtils';

import Tabs from '../Tabs/Tabs';
import Select from '../Select/Select';
import S from './LiveVideoStyles';
import WebRtcPlayer from './WebRtcPlayer';
import IvsPlayer from './IvsPlayer';
import HlsPlayer from './HlsPlayer';

// ---------- types ----------

type Props = {
  deviceId: string;
  VPNStatus?: any;
  deviceStatus?: any;
};

// ---------- constants ----------

const CAMERAS = [{
  id: 'outward',
  label: 'Outward camera',
  cameraId: 1,
}, {
  id: 'inward',
  label: 'Inward camera',
  cameraId: 2,
}, {
  id: 'multiple',
  label: 'Multiple cameras',
  cameraId: 0,
}];

const streamDurationOptions = [{
  label: '10s',
  value: '10000',
}, {
  label: '30s',
  value: '30000',
}, {
  label: '1m',
  value: '60000',
}, {
  label: 'Unlimited',
  value: '3600000',
}];

// The Amazon IVS Player SDK has a confirmed, undocumented bug on Firefox: its
// internal WASM worker keeps only a ~20-70ms buffer ahead of the playhead
// regardless of setLiveLowLatencyEnabled/setInitialBufferDuration, causing
// continuous rebuffering — verified via isolation testing against the same
// stream that plays perfectly in Chrome. hls.js manages MSE buffering
// independently of that worker and was verified to play the same IVS channel
// smoothly on Firefox, so it's used there instead of IvsPlayer.
function isFirefox(): boolean {
  return typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
}

// ---------- per-camera stream view ----------

type StreamViewProps = {
  deviceId: string;
  cameraId: number;
  streamVisibilityDuration: number;
  VPNStatus: any;
  isActive: any;
  deviceStatus: boolean;
  liveStreamProtocol: LiveStreamProtocol | undefined;
};

function StreamView({
  deviceId,
  cameraId,
  streamVisibilityDuration,
  VPNStatus,
  isActive,
  deviceStatus,
  liveStreamProtocol,
}: Readonly<StreamViewProps>): JSX.Element {
  // Wait until device settings are resolved before routing — avoids a flash
  // to the wrong player while the settings request is in flight.
  if (liveStreamProtocol === undefined) {
    return <S.VideoContainer><p className="video-preview">Loading...</p></S.VideoContainer>;
  }

  if (liveStreamProtocol === 'webrtc') {
    return (
      <WebRtcPlayer
        deviceId={deviceId}
        cameraId={cameraId}
        streamVisibilityDuration={streamVisibilityDuration}
        VPNStatus={VPNStatus}
        isActive={isActive}
        deviceStatus={deviceStatus}
      />
    );
  }

  // srt / rtmp → IVS playback path. Firefox uses the hls.js fallback — see
  // isFirefox()'s comment for why.
  if (isFirefox()) {
    return (
      <HlsPlayer
        deviceId={deviceId}
        cameraId={cameraId}
        deviceStatus={deviceStatus}
        streamVisibilityDuration={streamVisibilityDuration}
      />
    );
  }

  return (
    <IvsPlayer
      deviceId={deviceId}
      cameraId={cameraId}
      deviceStatus={deviceStatus}
      streamVisibilityDuration={streamVisibilityDuration}
    />
  );
}

// ---------- modal shell (tabs + duration picker) ----------

function WatchVideoModal({ deviceId, VPNStatus, deviceStatus }: Readonly<Props>): JSX.Element {
  const fleetId = useGetFleetId();
  const { data: deviceSettings } = useGetDeviceVideoSettings(deviceId, fleetId);
  const liveStreamProtocol = deviceSettings?.liveStreamProtocol;

  const [liveStreamDuration, setLiveStreamDuration] = useLocalStorage(
    getLocalStorageKey('live-stream-duration'),
    streamDurationOptions[1].value,
  );
  const [streamVisibilityDuration, setStreamVisibilityDuration] = useState(
    liveStreamDuration || streamDurationOptions[1].value,
  );
  const [activeTab, setActiveTab] = useState('outward');

  const streamDurationOnChange = (e: any): void => {
    setStreamVisibilityDuration(e.target.value);
    setLiveStreamDuration(e.target.value);
  };

  const buildTabContent = (cameraId: number): JSX.Element => (
    <StreamView
      deviceId={deviceId}
      cameraId={cameraId}
      streamVisibilityDuration={Number(streamVisibilityDuration)}
      VPNStatus={VPNStatus}
      isActive={activeTab === CAMERAS.find((c) => c.cameraId === cameraId)?.id}
      deviceStatus={deviceStatus}
      liveStreamProtocol={liveStreamProtocol}
    />
  );

  const setTabs = (): any => CAMERAS.map(({ id, label, cameraId }) => ({
    label,
    id,
    content: cameraId === 0 ? (
      <S.Wrapper multiple>
        {buildTabContent(1)}
        {buildTabContent(2)}
      </S.Wrapper>
    ) : (
      <S.Wrapper>
        {buildTabContent(cameraId)}
      </S.Wrapper>
    ),
  }));

  const tabsContent = useMemo(() => setTabs(), [
    activeTab,
    streamVisibilityDuration,
    VPNStatus,
    deviceId,
    deviceStatus,
    liveStreamProtocol,
  ]);

  return (
    <S.ModalStyle>
      <div className="select-stream-duration">
        <p>Choose streaming duration:</p>
        <Select
          value={streamVisibilityDuration as string}
          options={streamDurationOptions}
          onChange={streamDurationOnChange}
          simple
        />
      </div>
      <Tabs
        tabs={tabsContent}
        onChange={(id: any) => setActiveTab(id)}
      />
    </S.ModalStyle>
  );
}

export default WatchVideoModal;
